import React, { useCallback, useMemo } from "react";
import { ParticipantsButton, toFlatCommunicationIdentifier, useCallClient, useChatClient, usePropsFor } from "@azure/communication-react";
import {
    AllChatThreadsProvider,
    isAskTeacherThreadTopic,
    isGroupChatThreadTopic,
    pcpLiveFeatures,
    transformTopicForModerator, usePcpApiMethods,
    usePcpLiveLessonContext,
} from "live-lesson/shared/feature-call-ui-react";
import { ModeratorChatComponents } from "./ModeratorChatComponents";


export function ChatThreadSwitcher(props: { isOpen: boolean }): JSX.Element {
    const { isOpen } = props;

    const chatClient = useChatClient();

    const myId =  toFlatCommunicationIdentifier(useCallClient().getState().userId);
    const participantsProps = usePropsFor(ParticipantsButton);
    const participants = participantsProps.participants;

    const remoteParticipantIds = useMemo(
        () => participants.map(p => p.userId),
        [participants]
    );

    const chatState = chatClient.getState();
    const threads = useMemo(() => Object.values(chatState.threads)
        .filter(thread => {
            if (isAskTeacherThreadTopic(thread.properties?.topic)) {
                // check if the user is online
                // => filter out private chats with offline users
                for (const participant of Object.values(thread.participants)) {
                    const participantId = toFlatCommunicationIdentifier(participant.id);
                    if (participantId !== myId && remoteParticipantIds.includes(participantId)) {
                        return true;
                    }
                }
                return false;
            }

            return true;
        })
        .map((thread) => ({
            threadId: thread.threadId,
            topic: transformTopicForModerator(thread.properties?.topic),
            participants: Object.values(thread.participants).filter(p => toFlatCommunicationIdentifier(p.id) !== myId),
        }))
        .sort((a, b) => {
            // if the topic is "Gruppenchat", show it first
            if (isGroupChatThreadTopic(a.topic)) {
                return -1;
            }

            // order by topic ascending
            return a.topic.localeCompare(b.topic);
        }), [chatState.threads, myId, remoteParticipantIds]);

    const activateChat = usePcpLiveLessonContext(s => s.setActiveThreadId);
    const threadState = usePcpLiveLessonContext(s => s.chatThreadState);
    const activeThreadId = threadState.activeThread;


    const activateThread = useCallback((threadId: string) => {
        activateChat(threadId);
    }, [activateChat]);

    const pcpApi = usePcpApiMethods();
    const pcpApiDeleteMessage = pcpApi.deleteMessage;

    const renderChatThread = useCallback((threadId: string) =>
        <ModeratorChatComponents threadId={threadId} isOpen={isOpen} pcpApiDeleteMessage={pcpApiDeleteMessage} />,
        [isOpen]
    );

    return (
        <>
            {pcpLiveFeatures.privateChats && (
                <select aria-label="Chat auswählen" onChange={(e) => activateThread(e.target.value)} value={activeThreadId}>
                    {threads.map((thread) => (
                        <option key={thread.threadId} value={thread.threadId}>
                            {thread.topic}
                        </option>
                    ))}
                </select>
            )}
            <AllChatThreadsProvider renderChatThread={renderChatThread} />
        </>
    );
}


